<template>
  <BaseModal @close="$emit('close')">
    <ModalContainer>
      <ModalTitle>{{ title }}</ModalTitle>

      <ModalBody
        class="upload-status-modal__body"
        :class="{ 'upload-status-modal__body--wide': showDetail }"
      >
        <UploadCompletedInvalidUsersDetail
          v-if="isCompleted && showDetail"
          class="upload-status-modal__detail"
          :invalid-users="invalidUsers"
        />
        <UploadCompletedSummary
          v-else-if="isCompleted"
          :state="userUpload.state"
          :new-users-count="userUpload.new_users_count"
          :existing-users-count="userUpload.existing_users_count"
          :invalid-users-count="userUpload.invalid_users_count"
          :started-at="userUpload.started_at"
          :ended-at="userUpload.ended_at"
          :running-time="userUpload.running_time"
        />
        <UploadStatusBody
          v-else
          :completed-batches="userUpload.completed_batches"
          :number-of-batches="userUpload.number_of_batches"
        />
      </ModalBody>

      <ModalActions>
        <BaseButton outline @click="$emit('close')">
          {{ $t('CLOSE') }}
        </BaseButton>
        <BaseButton
          v-if="showDetail"
          theme="blue"
          @click="showDetail = false"
        >
          {{ $t('userUpload.SYNC_SUMMARY_BACK_TO_SUMMARY') }}
        </BaseButton>
        <BaseButton
          v-else-if="isCompleted && hasInvalidUsers"
          theme="blue"
          @click="showDetail = true"
        >
          {{ $t('userUpload.SYNC_SUMMARY_VIEW_DETAILS') }}
        </BaseButton>
      </ModalActions>
    </ModalContainer>
  </BaseModal>
</template>

<script setup lang="ts">
import BaseButton from '@/components/generics/base/BaseButton.vue'
import BaseModal from '@/components/generics/base/BaseModal.vue'
import ModalActions from '@/components/generics/modals/ModalActions.vue'
import ModalBody from '@/components/generics/modals/ModalBody.vue'
import ModalContainer from '@/components/generics/modals/ModalContainer.vue'
import ModalTitle from '@/components/generics/modals/ModalTitle.vue'
import { useI18n } from '@/hooks/useI18N'
import type {
  UserUploadState,
  UserUploadSummaryAttributes,
} from '@/queries/userUpload/userUploadTypes'
import { computed, ref } from 'vue'
import UploadCompletedInvalidUsersDetail from './UploadCompletedInvalidUsersDetail.vue'
import UploadCompletedSummary from './UploadCompletedSummary.vue'
import UploadStatusBody from './UploadStatusBody.vue'

const props = defineProps<{
  userUpload: UserUploadSummaryAttributes
}>()

defineEmits<{
  close: []
}>()

const i18n = useI18n()

const ONGOING_STATES: UserUploadState[] = ['initialized', 'started']

const showDetail = ref(false)

const isCompleted = computed(
  () => !ONGOING_STATES.includes(props.userUpload.state)
)

const invalidUsers = computed(() => props.userUpload.invalid_users)

const hasInvalidUsers = computed(() => invalidUsers.value.length > 0)

const title = computed(() => {
  if (!isCompleted.value) return i18n.t('userUpload.SYNC_STATUS_TITLE')
  if (showDetail.value)
    return i18n.t('userUpload.SYNC_SUMMARY_DETAILS_MODAL_TITLE')
  return i18n.t('userUpload.SYNC_SUMMARY_MODAL_TITLE')
})
</script>

<style lang="scss" scoped>
.upload-status-modal__body {
  transition: min-width $transition-superfast ease,
    max-width $transition-superfast ease;

  &--wide {
    @media (min-width: $tablet-width-max) {
      min-width: 1000px;
      max-width: 1000px;
    }
  }
}

.upload-status-modal__detail {
  opacity: 1;
  transition: opacity $transition-superfast ease $transition-superfast;

  @starting-style {
    opacity: 0;
  }
}
</style>
